import { useEffect, useState, useCallback } from "react";
import { analyticsApi } from "../lib/api";
import { TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Warm editorial palette — single accent family, no rainbow per category.
const CAT_COLORS = {
  Sculptures:        { dot: "#EA580C" },
  Paintings:         { dot: "#9333EA" },
  Jewelry:           { dot: "#DB2777" },
  Clothing:          { dot: "#0D9488" },
  Books:             { dot: "#2563EB" },
  "Home and Living": { dot: "#059669" },
};
const DEFAULT_DOT = "#A8A29E";

const PERIODS = [
  { value: "7d",  label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
];

function DeltaPill({ deltaPct, deltaAbs, previous }) {
  if (previous === 0 && (deltaAbs || 0) > 0) {
    return (
      <span data-testid="delta-pill-new" className="dash-delta-pill dash-delta-pill--new">
        <Sparkles className="w-3 h-3" /> NEW
      </span>
    );
  }
  if (deltaPct === null || deltaPct === undefined) {
    return (
      <span data-testid="delta-pill-none" className="dash-delta-pill dash-delta-pill--flat">
        <Minus className="w-3 h-3" /> —
      </span>
    );
  }
  if (deltaPct > 0) {
    return (
      <span data-testid="delta-pill-up" className="dash-delta-pill dash-delta-pill--up">
        <TrendingUp className="w-3 h-3" /> +{deltaPct}%
      </span>
    );
  }
  if (deltaPct < 0) {
    return (
      <span data-testid="delta-pill-down" className="dash-delta-pill dash-delta-pill--down">
        <TrendingDown className="w-3 h-3" /> {deltaPct}%
      </span>
    );
  }
  return (
    <span data-testid="delta-pill-flat" className="dash-delta-pill dash-delta-pill--flat">
      <Minus className="w-3 h-3" /> 0%
    </span>
  );
}

export default function CategoryIntentWidget() {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsApi.getCategoryIntent(period);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load category intent");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const periodLabel = period === "7d" ? "this week" : period === "30d" ? "this month" : "last 90 days";

  return (
    <section data-testid="category-intent-widget" className="dash-card">
      <div className="dash-card-head">
        <div>
          <p className="dash-section-eyebrow" style={{ marginBottom: 6 }}>Intent</p>
          <h3>What's trending in WhatsApp</h3>
          <p>Auto-detected from chat keywords, {periodLabel}</p>
        </div>
        <div className="dash-period-toggle">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              data-testid={`category-intent-period-${p.value}`}
              onClick={() => setPeriod(p.value)}
              className={`dash-period-btn ${period === p.value ? "active" : ""}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="dash-card-body">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`cat-skeleton-${i}`} className="h-28 rounded-2xl bg-[--accent-pale] animate-pulse" />
            ))}
          </div>
        ) : !data?.categories?.length ? (
          <p data-testid="category-intent-empty" className="text-sm text-[--text-muted] py-6 text-center">
            No category data yet — enquiries will surface here as they flow in.
          </p>
        ) : (
          <>
            {/* Summary strip */}
            <div className="flex items-center gap-4 text-[11px] text-[--text-muted] mb-5 flex-wrap">
              <span>
                <strong className="text-[--text-primary] tabular-nums">{data.totals.current_with_category}</strong> classified enquiries
              </span>
              <span className="text-[--accent-soft]">·</span>
              <span>
                <strong className="text-[--text-primary] tabular-nums">{data.totals.current_uncategorized}</strong> uncategorized
              </span>
              <span className="text-[--accent-soft]">·</span>
              <span>vs <strong className="text-[--text-primary] tabular-nums">{data.totals.previous_with_category}</strong> previous period</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.categories.map((cat) => {
                const dot = CAT_COLORS[cat.category]?.dot || DEFAULT_DOT;
                return (
                  <div
                    key={cat.category}
                    data-testid={`category-intent-card-${cat.category}`}
                    className="dash-intent-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: dot, boxShadow: `0 0 0 3px ${dot}22` }}
                        />
                        <span className="text-[13px] font-medium text-[--text-primary] truncate">
                          {cat.category}
                        </span>
                      </div>
                      <DeltaPill
                        deltaPct={cat.delta_pct}
                        deltaAbs={cat.delta_abs}
                        previous={cat.previous}
                      />
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span
                        className="text-3xl leading-none tabular-nums text-[--text-primary]"
                        style={{ fontFamily: "Playfair Display, serif" }}
                      >
                        {cat.current}
                      </span>
                      <span className="text-[11px] text-[--text-muted]">
                        {cat.current === 1 ? "enquiry" : "enquiries"}
                      </span>
                    </div>
                    {cat.top_countries.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {cat.top_countries.slice(0, 3).map((c) => (
                          <span
                            key={c.code}
                            data-testid={`category-intent-country-${cat.category}-${c.code}`}
                            className="dash-country-chip"
                            title={`${c.label}: ${c.count}`}
                          >
                            <span>{c.flag}</span>
                            <span className="truncate max-w-[80px]">{c.label}</span>
                            <span className="opacity-60 tabular-nums">{c.count}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[--text-muted] italic">No enquiries {periodLabel}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
